import { useState } from 'react'
import Layout from '../../components/feature/Layout'
import Card from '../../components/base/Card'
import Badge from '../../components/base/Badge'
import Button from '../../components/base/Button'
import Input from '../../components/base/Input'

interface Site {
  id: string
  name: string
  location: string
  status: 'online' | 'offline' | 'warning'
  lastUpdate: string
  alerts: number
  devices: number
  temperature: number
  humidity: number
}

export default function DashboardPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const sites: Site[] = [
    {
      id: 'site-001',
      name: 'Manufacturing Plant A',
      location: 'New York, NY',
      status: 'online',
      lastUpdate: '2 minutes ago',
      alerts: 0,
      devices: 24,
      temperature: 22.5,
      humidity: 45
    },
    {
      id: 'site-002',
      name: 'Warehouse B',
      location: 'Los Angeles, CA',
      status: 'warning',
      lastUpdate: '5 minutes ago',
      alerts: 2,
      devices: 18,
      temperature: 28.1,
      humidity: 62
    },
    {
      id: 'site-003',
      name: 'Data Center C',
      location: 'Chicago, IL',
      status: 'offline',
      lastUpdate: '1 hour ago',
      alerts: 5,
      devices: 32,
      temperature: 0,
      humidity: 0
    },
    {
      id: 'site-004',
      name: 'Office Building D',
      location: 'Miami, FL',
      status: 'online',
      lastUpdate: '1 minute ago',
      alerts: 0,
      devices: 16,
      temperature: 24.2,
      humidity: 55
    }
  ]

  const filteredSites = sites.filter((site) => {
    const matchesSearch =
      site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      site.location.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || site.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'online':
        return <Badge variant="success">Online</Badge>
      case 'warning':
        return <Badge variant="warning">Warning</Badge>
      case 'offline':
        return <Badge variant="danger">Offline</Badge>
      default:
        return <Badge variant="gray">Unknown</Badge>
    }
  }

  const totalSites = sites.length
  const onlineSites = sites.filter((s) => s.status === 'online').length
  const totalAlerts = sites.reduce((sum, site) => sum + site.alerts, 0)
  const totalDevices = sites.reduce((sum, site) => sum + site.devices, 0)

  return (
    <Layout title="Dashboard">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-building-line text-blue-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Sites</p>
              <p className="text-2xl font-bold text-gray-900">{totalSites}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Online Sites</p>
              <p className="text-2xl font-bold text-gray-900">{onlineSites}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-alarm-warning-line text-red-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Alerts</p>
              <p className="text-2xl font-bold text-gray-900">{totalAlerts}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-device-line text-purple-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Devices</p>
              <p className="text-2xl font-bold text-gray-900">{totalDevices}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Input
              placeholder="Search sites..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon="ri-search-line"
              className="sm:w-64"
            />

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 pr-8"
            >
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="warning">Warning</option>
              <option value="offline">Offline</option>
            </select>
          </div>

          <Button>
            <i className="ri-add-line mr-2"></i>
            Add Site
          </Button>
        </div>
      </Card>

      {/* Sites Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredSites.map((site) => (
          <Card key={site.id} className="hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{site.name}</h3>
                <p className="text-sm text-gray-500 flex items-center mt-1">
                  <i className="ri-map-pin-line mr-1"></i>
                  {site.location}
                </p>
              </div>
              {getStatusBadge(site.status)}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Temperature</p>
                <p className="text-lg font-semibold text-gray-900">
                  {site.temperature > 0 ? `${site.temperature}°C` : '--'}
                </p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Humidity</p>
                <p className="text-lg font-semibold text-gray-900">
                  {site.humidity > 0 ? `${site.humidity}%` : '--'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500">
              <span className="flex items-center">
                <i className="ri-device-line mr-1"></i>
                {site.devices} devices
              </span>
              <span className="flex items-center">
                <i className="ri-alarm-warning-line mr-1"></i>
                {site.alerts} alerts
              </span>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-xs text-gray-500">Updated {site.lastUpdate}</span>
              <Button variant="ghost" size="sm">
                View Details
                <i className="ri-arrow-right-line ml-1"></i>
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {filteredSites.length === 0 && (
        <Card className="text-center py-12">
          <i className="ri-search-line text-4xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sites found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
        </Card>
      )}
    </Layout>
  )
}
